<template>
  <TwinPanel title="Create your account">
    <template v-slot:form>
      <b-form :novalidate="true" class="form" @submit="onSubmit">
        <b-alert
          v-for="error in errors"
          :key="error"
          variant="danger"
          :show="true"
        >
          {{ error }}
        </b-alert>

        <b-form-group label="Full name" label-for="userName">
          <b-form-input
            id="userName"
            v-model="user.name"
            required
            :state="nameValid"
          />
          <b-form-invalid-feedback :state="nameValid">
            Please type your full name.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Company" label-for="userCompany">
          <b-form-input
            id="userCompany"
            v-model="user.company"
            required
            :state="companyValid"
          />
          <b-form-invalid-feedback :state="companyValid">
            Please type your company name.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Role" label-for="userRole">
          <b-form-input
            id="userRole"
            v-model="user.role"
          />
        </b-form-group>

        <b-form-group label="Company email address" label-for="userEmail">
          <b-form-input
            id="userEmail"
            v-model="user.email"
            debounce="350"
            required
            :state="emailValid"
          />
          <b-form-invalid-feedback :state="emailValid">
            This doesn't look like an email to me...
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Password" label-for="userPassword">
          <b-form-input
            id="userPassword"
            v-model="user.password"
            type="password"
            debounce="350"
            required
            :state="passwordValid"
          />
          <b-form-invalid-feedback :state="passwordValid">
            {{ passwordErrorMessage() }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-checkbox
          v-model="user.marketing_emails"
          :unchecked-value="false"
        >
          By checking this box you agree to receive relevant news and personal emails from Cyber-Duck (the digital transformation and UX agency that owns Heurix). You can unsubscribe at any time. For more details review our <a href="https://www.heurix.io/privacy-policy/" target="_blank">privacy policy</a>.
        </b-form-checkbox>

        <b-button
          class="mt-3 mb-3"
          type="submit"
          variant="primary"
          :pressed="submitting"
          :disabled="
            submitting ||
              !nameValid ||
              !companyValid ||
              !emailValid ||
              !passwordValid ||
              !user.marketing_emails
          "
        >
          Create your account
        </b-button>

        <b-form-group>
          Already have an account?
          <a
            :href="resolve('user.login')"
            @click="navigate('user.login', $event)"
            >Log In</a
          >
        </b-form-group>
      </b-form>
    </template>
    <template v-slot:text>
      <h4>Create an account in under 30 seconds.</h4>
      <h4>
        You'll be on your way to heuristic evaluation bliss in the blink of an
        eye.
      </h4>
    </template>
  </TwinPanel>
</template>

<style lang="scss" scoped>
.form {
  margin: 0 auto;
  max-width: 550px;

  input,
  button {
    height: 3em;
    width: 100%;
  }
}
</style>

<script>
import isEmail from "validator/lib/isEmail";
import apiMixin from "../../utils/api";
import TwinPanel from "./utils/TwinPanel";

let invalidPasswords = ["something else", "at least 10 characters"];

export default {
  name: "UserRegister",
  components: { TwinPanel },
  mixins: [apiMixin],
  data() {
    return {
      user: { marketing_emails: true, role: "" },
      submitting: false,
      success: false,
    };
  },
  computed: {
    nameValid() {
      if (!this.user.name) {
        return null;
      }
      return this.user.name && this.user.name.trim().length >= 1;
    },
    companyValid() {
      if (!this.user.company) {
        return null;
      }
      return this.user.company && this.user.company.trim().length >= 1;
    },
    emailValid() {
      if (!this.user.email) {
        return null;
      }
      return isEmail(this.user.email);
    },
    passwordValid() {
      if (!this.user.password) {
        return null;
      }
      return (
        this.user.password &&
        this.user.password.length >= 10 &&
        !invalidPasswords.includes(this.user.password.trim())
      );
    },
  },
  async created() {
    let maybeReroute = (val) => {
      if (val) {
        this.navigate("site.list");
      }
    };
    this.$store.watch(
      (state, getters) => getters["user/loggedIn"],
      (newValue) => {
        maybeReroute(newValue);
      }
    );
    maybeReroute(this.$store.getters["user/loggedIn"]);
  },
  methods: {
    passwordErrorMessage() {
      if (!this.user.password) {
        return;
      }
      switch (this.user.password.trim()) {
        case "at least 10 characters":
          return "Hahaha, hilarious. But seriously, you can't use that. Pick something else.";
        case "something else":
          return ".... uh-huh, keep trying bub.";
        default:
          return "Your password must be at least 10 characters. Safety first!";
      }
    },
    async onSubmit(e) {
      e.preventDefault();
      this.withLoading(async () => {
        try {
          await this.post("/api/users/", this.user);
          await this.$store.dispatch("user/getDetails", this);

          if (this.$gtm.enabled()) {
            this.$gtm.trackEvent({
              event: "register",
              value: this.user.email,
            });
          }

          await this.$router.push({ name: "site.create" });
        } catch (e) {
          this.catchApiError(e, false);
        }
      }, "submitting");
    },
  },
};
</script>
